// HomePage.js

import React, { useEffect, useState } from 'react';
import { useAuth } from "../../context/DBContext";
import { createManagePortal, GetCustomClaimRole } from '../../api/stripe';
import { useNavigate } from 'react-router-dom';
import { Layout, Button, Typography, Row, Col, Card, List, Space, Spin, Tooltip, message as antMessage, Tabs, Statistic, Descriptions, Tag, Empty, Modal } from 'antd';
import { HeartFilled, LoadingOutlined, ReadOutlined, UserOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import ProfileForm from '../signupPage/ProfileForm';

import 'react-calendar/dist/Calendar.css';

const { Content } = Layout;
const { Title, Text } = Typography;

const HomePage = () => {
  const { 
    currentUser, 
    fetchUpcomingBookings, 
    userInfo, 
    fetchServiceById, 
    fetchProducts, 
    removeFavoriteService, 
    removeFavoriteProduct, 
    updateUser, 
    setUserInfo 
  } = useAuth();

  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const [subRole, setSubRole] = useState();
  const navigate = useNavigate();

  // Modified booking states
  const [childrenBookings, setChildrenBookings] = useState(new Map());
  const [childrenBookingsLoading, setChildrenBookingsLoading] = useState(new Map());
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);

  // Favorite services and products
  const [favoriteServices, setFavoriteServices] = useState([]);
  const [favoriteProducts, setFavoriteProducts] = useState([]);

  // Pagination state
  const [currentServices, setCurrentServices] = useState([]);
  const [currentProducts, setCurrentProducts] = useState([]);
  const [currentBookings, setCurrentBookings] = useState([]);

  // Replace the old loading states with new granular ones
  const [roleLoading, setRoleLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);

  // Section loading component
  const SectionLoading = () => (
    <div className="flex justify-center items-center p-8" style={{ minHeight: '200px' }}>
      <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
    </div>
  );

  useEffect(() => {
    const fetchUserRoleAndBookings = async () => {
      // console.log("Starting data fetch...");
      
      if (!currentUser || !userInfo) {
        // console.log("No current user or user info found.");
        setRoleLoading(false);
        setBookingsLoading(false);
        setServicesLoading(false);
        setProductsLoading(false);
        setPageLoading(false);
        return;
      }

      try {
        // 1. Fetch user role
        setRoleLoading(true);
        const userRole = await GetCustomClaimRole();
        setSubRole(userRole);
        // console.log("User Role:", userRole);
        setRoleLoading(false);

        // 2. Fetch bookings
        // console.log("Fetching bookings...");
        setBookingsLoading(true);
        try {
          if (userInfo.accountType === 'children' && userInfo.children) {
            // Initialize loading states and bookings map
            const loadingStates = new Map();
            const bookingsMap = new Map();
            
            // Set initial loading state for each child
            userInfo.children.forEach(child => {
              loadingStates.set(child.id, false);
            });
            setChildrenBookingsLoading(loadingStates);

            // Fetch bookings for each child
            const bookingPromises = userInfo.children.map(async (child) => {
              try {
                loadingStates.set(child.id, true);
                setChildrenBookingsLoading(new Map(loadingStates));
                
                // console.log(`Fetching bookings for child ${child.id}...`);
                const childBookings = await fetchUpcomingBookings(currentUser.uid, child.id);
                // console.log(`Received bookings for child ${child.id}:`, childBookings);
                bookingsMap.set(child.id, childBookings || []);
                // console.log('Current bookingsMap:', Array.from(bookingsMap.entries()));
              } catch (error) {
                // console.error(`Error fetching bookings for child ${child.id}:`, error);
                bookingsMap.set(child.id, []);
              } finally {
                loadingStates.set(child.id, false);
                setChildrenBookingsLoading(new Map(loadingStates));
              }
            });

            await Promise.all(bookingPromises);
            // console.log('Final bookingsMap before setting state:', Array.from(bookingsMap.entries()));
            setChildrenBookings(new Map(bookingsMap));
          } else {
            // For non-children accounts, fetch bookings as before
            const bookings = await fetchUpcomingBookings(currentUser.uid);
            setUpcomingBookings(bookings || []);
          }
        } catch (error) {
          // console.error("Error fetching bookings:", error);
          setUpcomingBookings([]);
          setChildrenBookings(new Map());
        } finally {
          setBookingsLoading(false);
        }

        // 3. Fetch favorites (services)
        // console.log("Fetching favorite services...");
        setServicesLoading(true);
        try {
          const services = await fetchFavoriteServices();
          setFavoriteServices(services || []);
          // console.log("Favorites fetched - Services:", services);
        } catch (error) {
          // console.error("Error fetching favorite services:", error);
          setFavoriteServices([]);
        } finally {
          setServicesLoading(false);
        }

        // 4. Fetch favorites (products)
        // console.log("Fetching favorite products...");
        setProductsLoading(true);
        try {
          const products = await fetchFavoriteProducts();
          setFavoriteProducts(products || []);
          // console.log("Favorites fetched - Products:", products);
        } catch (error) {
          // console.error("Error fetching favorite products:", error);
          setFavoriteProducts([]);
        } finally {
          setProductsLoading(false);
          setPageLoading(false);
        }

      } catch (error) {
        // console.error("Error in main fetch flow:", error);
        setUpcomingBookings([]);
        setFavoriteServices([]);
        setFavoriteProducts([]);
        setBookingsLoading(false);
        setServicesLoading(false);
        setProductsLoading(false);
        setPageLoading(false);
      }
    };

    fetchUserRoleAndBookings();
  }, [currentUser, fetchUpcomingBookings]);

  // Function to fetch favorite services
  const fetchFavoriteServices = async () => {
    const favoriteServicesIds = userInfo?.favoriteServices || [];
    if (favoriteServicesIds.length === 0) return [];

    try {
      const servicesPromises = favoriteServicesIds.map(id => fetchServiceById(id));
      const services = await Promise.all(servicesPromises);
      return services.filter(service => service !== null);
    } catch (error) {
      // console.error("Error fetching favorite services:", error);
      return [];
    }
  };

  // Function to fetch favorite products
  const fetchFavoriteProducts = async () => {
    const favoriteProductsIds = userInfo?.favoriteProducts || [];
    if (favoriteProductsIds.length === 0) return [];

    try {
      const products = await fetchProducts();
      const filteredProducts = products.filter(product => favoriteProductsIds.includes(product.id));
      return filteredProducts;
    } catch (error) {
      // console.error("Error fetching favorite products:", error);
      return [];
    }
  };

  // Update pagination when favorites change
  useEffect(() => {
    setCurrentServices(favoriteServices.slice(0, 2));
  }, [favoriteServices]);

  useEffect(() => {
    setCurrentProducts(favoriteProducts.slice(0, 2));
  }, [favoriteProducts]);

  useEffect(() => {
    setCurrentBookings(upcomingBookings.slice(0, 2));
  }, [upcomingBookings]);

  const loadMoreServices = () => {
    setCurrentServices(favoriteServices);
  };

  const loadMoreProducts = () => {
    setCurrentProducts(favoriteProducts);
  };

  const loadMoreBookings = () => {
    setCurrentBookings(upcomingBookings);
  };

  const handleManagePortal = async () => {
    setLoading(true);
    await createManagePortal();
    setLoading(false);
  };

  // Profile section component
  const ProfileSection = ({ 
    profile, 
    bookings = [], 
    onUpdateProfile,
    accountType,
    isLoading = false
  }) => {
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editedProfile, setEditedProfile] = useState(profile);
    const [isSaving, setIsSaving] = useState(false);

    const handleProfileChange = (e) => {
      const { name, value } = e.target;
      setEditedProfile(prev => {
        const newProfile = {
          ...prev,
          [name]: value
        };

        // If dateOfBirth is changed, update the age group
        if (name === 'dateOfBirth') {
          const today = new Date();
          const birthDate = new Date(value);
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }

          // Determine age group
          let ageGroup;
          if (age >= 1 && age <= 5) {
            ageGroup = "Early years (1-5)";
          } else if (age >= 6 && age <= 13) {
            ageGroup = "Primary (6-13)";
          } else if (age >= 14 && age <= 18) {
            ageGroup = "Preparing For Adulthood (14-18)";
          } else if (age >= 18 && age <= 25) {
            ageGroup = "Young Adults (18-25)";
          } else {
            ageGroup = "Adult (25+)";
          }

          newProfile.age = ageGroup;
        }

        return newProfile;
      });
    };

    const handleSave = () => {
      setIsSaving(true);
      // Set a minimum loading time
      setTimeout(() => {
        onUpdateProfile(editedProfile)
          .then(() => {
            setIsEditModalVisible(false);
            antMessage.success('Profile updated successfully');
          })
          .catch((error) => {
            // console.error('Error updating profile:', error);
            antMessage.error('Failed to update profile');
          })
          .finally(() => {
            setIsSaving(false);
          });
      }, 1200);
    };

    return (
      <>
        <Row gutter={[24, 24]}>
          {/* Profile Information Column */}
          <Col xs={24} lg={14}>
            <Card 
              title="Profile Information" 
              bodyStyle={{ minHeight: '400px' }}
              extra={
                <Button 
                  type="link" 
                  icon={<EditOutlined />}
                  onClick={() => setIsEditModalVisible(true)}
                >
                  Edit Profile
                </Button>
              }
            >
              <Row gutter={[16, 24]}>
                {/* Top Stats Row */}
                <Col span={24}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic 
                        title={<Text style={{ fontSize: '12px' }}>Age Group</Text>} 
                        value={profile.age}
                        valueStyle={{ fontSize: '14px' }}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic 
                        title={<Text style={{ fontSize: '12px' }}>Education Status</Text>} 
                        value={profile.educationStatus}
                        valueStyle={{ fontSize: '14px' }}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic 
                        title={<Text style={{ fontSize: '12px' }}>Disability</Text>} 
                        value={profile.disability}
                        valueStyle={{ fontSize: '14px' }}
                      />
                      {profile.disabilityOther && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>{profile.disabilityOther}</Text>
                      )}
                    </Col>
                  </Row>
                </Col>

                {/* Two Column Layout for Details */}
                <Col span={12}>
                  <Title level={5}>Personal Information</Title>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Date of Birth">
                      {profile.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }) : ''}
                    </Descriptions.Item>
                    <Descriptions.Item label="Gender">
                      {profile.gender === 'other' ? profile.genderOther : profile.gender}
                    </Descriptions.Item>
                    <Descriptions.Item label="Ethnicity">
                      {profile.ethnicity === 'Other' ? profile.ethnicityOther : profile.ethnicity}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>

                <Col span={12}>
                  <Title level={5}>Support & Assistance</Title>
                  <Space direction="vertical" size="small">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text style={{ width: '45px', fontSize: '12px' }}>EHCP:</Text>
                      <Tag color={profile.ehcp === 'yes' ? 'blue' : 'default'}>
                        {profile.ehcp === 'yes' ? '✓' : '✗'}
                      </Tag>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text style={{ width: '45px', fontSize: '12px' }}>DLA:</Text>
                      <Tag color={profile.dla === 'yes' ? 'blue' : 'default'}>
                        {profile.dla === 'yes' ? '✓' : '✗'}
                      </Tag>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text style={{ width: '45px', fontSize: '12px' }}>PIP:</Text>
                      <Tag color={profile.pip === 'yes' ? 'blue' : 'default'}>
                        {profile.pip === 'yes' ? '✓' : '✗'}
                      </Tag>
                    </div>
                  </Space>
                </Col>

                {/* Interests at the Bottom */}
                <Col span={24}>
                  <Title level={5}>Interests</Title>
                  <Space wrap>
                    {profile.interests?.map(interest => (
                      <Tag key={interest} color="blue">{interest}</Tag>
                    ))}
                  </Space>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Upcoming Bookings Column */}
          <Col xs={24} lg={10}>
            <Card 
              title="Upcoming Bookings"
              bodyStyle={{ minHeight: '400px' }}
            >
              {isLoading ? (
                <SectionLoading />
              ) : bookings.length === 0 ? (
                <Empty description="No upcoming bookings" />
              ) : (
                <List
                  itemLayout="vertical"
                  dataSource={bookings}
                  renderItem={item => (
                    <List.Item
                      actions={[
                        <Button 
                          icon={<ReadOutlined />} 
                          type="primary" 
                          onClick={() => navigate(`/service/${item.serviceId}`)}
                        >
                          View Service
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        title={<a href={`/service/${item.serviceId}`}>{item.serviceName}</a>}
                        description={
                          <Space direction="vertical">
                            <Text>Date: {item.date}</Text>
                            <Text>Time: {item.startTime} - {item.endTime}</Text>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </Card>
          </Col>
        </Row>

        {/* Edit Profile Modal */}
        <Modal
          title="Edit Profile"
          open={isEditModalVisible}
          onCancel={() => setIsEditModalVisible(false)}
          width={800}
          footer={[
            <Button key="cancel" onClick={() => setIsEditModalVisible(false)}>
              Cancel
            </Button>,
            <Button 
              key="save" 
              type="primary" 
              onClick={handleSave}
              loading={isSaving}
            >
              Save Changes
            </Button>
          ]}
        >
          <ProfileForm
            profile={editedProfile}
            onChange={handleProfileChange}
            isChild={accountType === 'children'}
            isModal={true}
          />
        </Modal>
      </>
    );
  };

  // Common sections (subscription, favorites)
  const SubscriptionSection = () => (
    <Card title="Your Subscription" style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text>
          You are currently subscribed to the{' '}
          <Text style={{ color: '#1890ff', fontWeight: 'bold' }}>
            {subRole || 'Basic'}
          </Text> plan
        </Text>
        <Button type="primary" onClick={handleManagePortal} loading={loading}>
          Manage Subscription
        </Button>
      </div>
    </Card>
  );

  const FavoritesSection = () => (
    <Row gutter={16}>
      {/* Favorite Services Section */}
      <Col xs={24} sm={24} md={12}>
        <Card title="Your Favourite Services" bodyStyle={{ minHeight: '250px' }}>
          {servicesLoading ? (
            <SectionLoading />
          ) : favoriteServices.length === 0 ? (
            <div className="flex justify-center items-center" style={{ height: '200px' }}>
              <Text>No favourite services found.</Text>
            </div>
          ) : (
            <List
              itemLayout="horizontal"
              dataSource={currentServices}
              renderItem={item => (
                <List.Item
                  actions={[
                    <Tooltip title="Unfavorite Service">
                      <Button 
                        icon={<HeartFilled />} 
                        danger 
                        onClick={() => {
                          removeFavoriteService(userInfo.id, item.id)
                            .then(() => {
                              antMessage.success('Service removed from favorites.');
                              setFavoriteServices(prev => prev.filter(service => service.id !== item.id));
                            })
                            .catch(err => {
                              antMessage.error('Failed to remove favorite.');
                            });
                        }}
                      />
                    </Tooltip>
                  ]}
                >
                  <List.Item.Meta
                    title={<a href={`/service/${item.id}`}>{item.name}</a>}
                    description={`Category: ${Array.isArray(item.category) ? item.category.join(', ') : item.category}`}
                  />
                </List.Item>
              )}
            />
          )}
          {currentServices.length < favoriteServices.length && (
            <div style={{ textAlign: 'center', padding: '10px' }}>
              <Text
                style={{ color: '#1890ff', cursor: 'pointer' }}
                onClick={loadMoreServices}
              >
                Load More Services
              </Text>
            </div>
          )}
        </Card>
      </Col>

      {/* Favorite Products Section */}
      <Col xs={24} sm={24} md={12}>
        <Card title="Your Favorite Products" bodyStyle={{ minHeight: '250px' }}>
          {productsLoading ? (
            <SectionLoading />
          ) : favoriteProducts.length === 0 ? (
            <div className="flex justify-center items-center" style={{ height: '200px' }}>
              <Text>No favorite products found.</Text>
            </div>
          ) : (
            <List
              itemLayout="horizontal"
              dataSource={currentProducts}
              renderItem={item => (
                <List.Item
                  actions={[
                    <Tooltip title="Unfavorite Product">
                      <Button 
                        icon={<HeartFilled />} 
                        danger 
                        onClick={() => {
                          removeFavoriteProduct(userInfo.id, item.id)
                            .then(() => {
                              antMessage.success('Product removed from favorites.');
                              setFavoriteProducts(prev => prev.filter(product => product.id !== item.id));
                            })
                            .catch(err => {
                              antMessage.error('Failed to remove favorite.');
                            });
                        }}
                      />
                    </Tooltip>
                  ]}
                >
                  <List.Item.Meta
                    title={<a href={`/products`}>{item.name}</a>}
                    description={`Category: ${item.category}`}
                  />
                </List.Item>
              )}
            />
          )}
          {currentProducts.length < favoriteProducts.length && (
            <div style={{ textAlign: 'center', padding: '10px' }}>
              <Text
                style={{ color: '#1890ff', cursor: 'pointer' }}
                onClick={loadMoreProducts}
              >
                Load More Products
              </Text>
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );

  // Profile update handler
  const handleProfileUpdate = async (editedProfile, childId = null) => {
    return new Promise(async (resolve, reject) => {
      try {
        if (userInfo.accountType === 'self') {
          await updateUser(currentUser.uid, { profile: editedProfile });
        } else if (childId !== null) {
          const updatedChildren = userInfo.children.map(child => 
            child.id === childId ? { ...child, ...editedProfile } : child
          );
          await updateUser(currentUser.uid, { children: updatedChildren });
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };

  return (
    <>
      {roleLoading ? (
        <div className="flex justify-center items-center h-screen">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      ) : (
        <div style={{ padding: '24px' }}>
          {/* Welcome Section */}
          <div style={{ marginBottom: '32px' }}>
            <Title level={4} style={{ fontWeight: 'normal', margin: 0 }}>Welcome back,</Title>
            <Title level={2} style={{ fontWeight: 'bold', margin: 0 }}>{currentUser.displayName}</Title>
          </div>

          {/* Subscription Section */}
          <SubscriptionSection />

          {/* Profile Section */}
          <div style={{ marginBottom: '24px' }}>
            {userInfo?.accountType === 'self' ? (
              // Self Account Profile
              <ProfileSection 
                profile={userInfo.profile} 
                onUpdateProfile={handleProfileUpdate}
                accountType={userInfo.accountType}
                bookings={upcomingBookings}
                isLoading={bookingsLoading}
              />
            ) : (
              // Children Account Profiles
              <Tabs
                type="card"
                items={userInfo?.children?.map((child) => {
                  // console.log(`Rendering tab for child ${child.id}, bookings:`, childrenBookings.get(child.id));
                  return ({
                    label: (
                      <span>
                        <UserOutlined />
                        {child.firstName} {child.lastName}
                      </span>
                    ),
                    key: child.id,
                    children: (
                      <ProfileSection 
                        profile={child} 
                        onUpdateProfile={(editedProfile) => handleProfileUpdate(editedProfile, child.id)}
                        accountType={userInfo.accountType}
                        bookings={childrenBookings.get(child.id) || []}
                        isLoading={childrenBookingsLoading.get(child.id)}
                      />
                    )
                  });
                })}
              />
            )}
          </div>

          {/* Favorites Section */}
          <FavoritesSection />
        </div>
      )}
    </>
  );
};

export default HomePage;
